import React, { useState } from 'react';
import { Button, Dropdown, Icon, Input } from 'semantic-ui-react';
import { FILAS_VTME_THEME } from './filasVtmePageTheme';
import FilasVtmeHelpPopup from './FilasVtmeHelpPopup';
import {
  ANCHOR_OPTIONS,
  FilasVtmeRecorteState,
  MODE_OPTIONS,
  PERIOD_OPTIONS,
  RECORTE_EXAMPLES,
  SCOPE_OPTIONS,
  RecorteDateAnchor,
  RecorteMode,
  RecortePeriodPreset,
  RecorteScope,
  recorteSummary,
} from './filasVtmeRecorte';

type Props = {
  value: FilasVtmeRecorteState;
  onChange: (next: FilasVtmeRecorteState) => void;
  dirty?: boolean;
  saving?: boolean;
  onSave?: () => void;
};

const fieldWrap: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 160,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: FILAS_VTME_THEME.muted,
  display: 'flex',
  alignItems: 'center',
};

const hintStyle: React.CSSProperties = {
  marginTop: 10,
  padding: '8px 10px',
  background: '#f7f9fc',
  border: `1px solid ${FILAS_VTME_THEME.border}`,
  borderRadius: 6,
  fontSize: 12,
  color: FILAS_VTME_THEME.text,
  lineHeight: 1.45,
};

const optionContent = (text: string, help: string) => (
  <div
    style={{
      maxWidth: 'min(280px, calc(100vw - 48px))',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    }}
  >
    <div style={{ fontWeight: 600 }}>{text}</div>
    <div style={{ fontSize: 11, color: FILAS_VTME_THEME.muted, marginTop: 2, lineHeight: 1.35 }}>{help}</div>
  </div>
);

const FilasVtmeRecorteBar: React.FC<Props> = ({
  value,
  onChange,
  dirty = false,
  saving = false,
  onSave,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const anchorDisabled = value.mode === 'live';
  const customPeriod = value.periodPreset === 'custom';

  const patch = (partial: Partial<FilasVtmeRecorteState>) => onChange({ ...value, ...partial });

  const modeHelp = MODE_OPTIONS.find(o => o.value === value.mode)?.help ?? '';
  const anchorHelp = anchorDisabled
    ? 'No modo Fila agora a âncora fica desligada: o snapshot é ao vivo. Se houver período, o recorte usa a data de inserção no VTME.'
    : ANCHOR_OPTIONS.find(o => o.value === value.dateAnchor)?.help ?? '';
  const periodHelp = PERIOD_OPTIONS.find(o => o.value === value.periodPreset)?.help ?? '';
  const scopeHelp = SCOPE_OPTIONS.find(o => o.value === value.scope)?.help ?? '';
  const examples = RECORTE_EXAMPLES[value.mode] ?? [];

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${FILAS_VTME_THEME.border}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 700, color: FILAS_VTME_THEME.text }}>
          Recorte da visão
          <FilasVtmeHelpPopup
            text="Define qual universo de pedidos entra nos KPIs e/ou na tabela. Passe o mouse nos ? de cada campo ou abra “Entenda o recorte”."
            position="bottom left"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: FILAS_VTME_THEME.muted }}>{recorteSummary(value)}</div>
          {onSave && (
            <Button
              type="button"
              size="mini"
              primary
              disabled={!dirty || saving}
              loading={saving}
              onClick={() => onSave()}
              content={dirty ? 'Salvar filtros' : 'Filtros salvos'}
            />
          )}
          <button
            type="button"
            onClick={() => setHelpOpen(open => !open)}
            style={{
              border: 'none',
              background: 'transparent',
              color: FILAS_VTME_THEME.accentLilac,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name={helpOpen ? 'chevron up' : 'chevron down'} fitted />
            Entenda o recorte
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={fieldWrap}>
          <div style={labelStyle}>
            Modo
            <FilasVtmeHelpPopup text={modeHelp} position="bottom left" />
          </div>
          <Dropdown
            selection
            fluid
            options={MODE_OPTIONS.map(o => ({
              key: o.value,
              value: o.value,
              text: o.text,
              content: optionContent(o.text, o.help),
            }))}
            value={value.mode}
            onChange={(_, data) => patch({ mode: data.value as RecorteMode })}
          />
        </div>

        <div style={{ ...fieldWrap, opacity: anchorDisabled ? 0.55 : 1 }}>
          <div style={labelStyle}>
            Âncora da data
            <FilasVtmeHelpPopup text={anchorHelp} position="bottom left" />
          </div>
          <Dropdown
            selection
            fluid
            disabled={anchorDisabled}
            options={ANCHOR_OPTIONS.map(o => ({
              key: o.value,
              value: o.value,
              text: o.text,
              content: optionContent(o.text, o.help),
            }))}
            value={value.dateAnchor}
            onChange={(_, data) => patch({ dateAnchor: data.value as RecorteDateAnchor })}
          />
        </div>

        <div style={fieldWrap}>
          <div style={labelStyle}>
            Período
            <FilasVtmeHelpPopup
              text={
                value.mode === 'live'
                  ? `${periodHelp} Em Fila agora o período (se não for “Todos”) filtra pela inserção no VTME.`
                  : `${periodHelp} A janela é aplicada sobre a âncora escolhida.`
              }
              position="bottom center"
            />
          </div>
          <Dropdown
            selection
            fluid
            options={PERIOD_OPTIONS.map(o => ({
              key: o.value,
              value: o.value,
              text: o.text,
              content: optionContent(o.text, o.help),
            }))}
            value={value.periodPreset}
            onChange={(_, data) => patch({ periodPreset: data.value as RecortePeriodPreset })}
          />
        </div>

        {customPeriod && (
          <>
            <div style={fieldWrap}>
              <div style={labelStyle}>De</div>
              <Input
                type="date"
                value={value.fromDate}
                onChange={(_, data) => patch({ fromDate: String(data.value || '') })}
              />
            </div>
            <div style={fieldWrap}>
              <div style={labelStyle}>Até</div>
              <Input
                type="date"
                value={value.toDate}
                onChange={(_, data) => patch({ toDate: String(data.value || '') })}
              />
            </div>
          </>
        )}

        <div style={{ ...fieldWrap, minWidth: 180 }}>
          <div style={labelStyle}>
            Escopo
            <FilasVtmeHelpPopup text={scopeHelp} position="bottom right" />
          </div>
          <Dropdown
            selection
            fluid
            options={SCOPE_OPTIONS.map(o => ({
              key: o.value,
              value: o.value,
              text: o.text,
              content: optionContent(o.text, o.help),
            }))}
            value={value.scope}
            onChange={(_, data) => patch({ scope: data.value as RecorteScope })}
          />
        </div>
      </div>

      <div style={hintStyle}>
        <strong style={{ fontWeight: 700 }}>Agora:</strong> {modeHelp}
        {value.mode !== 'live' && (
          <>
            {' '}
            <span style={{ color: FILAS_VTME_THEME.muted }}>· Âncora:</span> {anchorHelp}
          </>
        )}{' '}
        <span style={{ color: FILAS_VTME_THEME.muted }}>· Escopo:</span> {scopeHelp}
      </div>

      {helpOpen && (
        <div style={{ ...hintStyle, marginTop: 8, background: '#eef6ff' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Como pensar nos filtros</div>
          <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
            <li>
              <strong>Modo</strong> escolhe o universo (só vivos, só ciclos fechados, ou os dois).
            </li>
            <li>
              <strong>Âncora</strong> diz <em>qual data</em> do pedido entra na janela (inserção, entrada ou saída
              da fila). Em Fila agora, período usa inserção.
            </li>
            <li>
              <strong>Período</strong> é a janela de tempo sobre essa âncora.
            </li>
            <li>
              <strong>Escopo</strong> diz se KPIs e tabela compartilham o mesmo universo ou se um dos dois fica na
              fila ao vivo.
            </li>
          </ul>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Exemplos neste modo</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {examples.map(example => (
              <li key={example} style={{ marginBottom: 4 }}>
                {example}
              </li>
            ))}
          </ul>
        </div>
      )}

      {value.scope !== 'both' && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#f57f17' }}>
          Escopo parcial ativo: KPIs e tabela podem mostrar universos diferentes. Prefira “KPIs + tabela” no uso
          diário.
        </div>
      )}
    </div>
  );
};

export default FilasVtmeRecorteBar;
